import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  DrillDownState, 
  DrillDownPath, 
  DrillDownData, 
  DrillDownRequest,
  DrillDownResponse,
  DrillDownEvent,
  KpiType,
  DrillDownLevel,
  DrillDownFilter,
  DrillDownBreadcrumb,
  TrendDataPoint
} from '../types/drilldown';
import { DRILL_DOWN_CONFIGS, getNextLevel, getPreviousLevel } from '../config/drilldownConfig';
import DataConsistencyService from '../services/DataConsistencyService';
import { 
  getAllDepartments, 
  getAllLocations, 
  generateLocalizedName,
  getRandomJobTitle,
  getRandomSkills
} from '../utils/localizedMockData';

// Initial state
const initialState: DrillDownState = {
  currentPath: null,
  data: null,
  loading: false,
  error: null,
  history: [],
  availableInteractions: []
};

// Async thunk for drill-down data fetching
export const fetchDrillDownData = createAsyncThunk(
  'drilldown/fetchData',
  async (request: DrillDownRequest) => {
    console.log('🚀 fetchDrillDownData started with request:', request);
    
    // Simulate API call with realistic delay for data processing
    await new Promise(resolve => setTimeout(resolve, 1200 + Math.random() * 800)); // 1.2-2.0 seconds
    
    // Mock data generation based on request
    const mockData = generateMockDrillDownData(request);
    console.log('🎯 Mock data generated:', mockData);
    
    const response: DrillDownResponse = {
      success: true,
      data: mockData,
      metadata: {
        queryTime: Math.random() * 200 + 50,
        cacheHit: Math.random() > 0.7,
        recordCount: mockData.breakdowns.length,
        hasMore: mockData.breakdowns.length >= 50
      }
    };
    
    console.log('📦 Returning response:', response);
    return response;
  }
);

// Async thunk for navigating drill-down levels
export const navigateDrillDown = createAsyncThunk(
  'drilldown/navigate',
  async (params: {
    kpi: KpiType;
    level: DrillDownLevel;
    selectedValue?: string | number;
    filters?: DrillDownFilter[];
    userContext: {
      userId: string;
      role: string;
      permissions: string[];
      department?: string;
    };
  }) => {
    console.log('🧭 navigateDrillDown started with params:', params);
    
    const { kpi, level, selectedValue, filters = [], userContext } = params;
    
    const request: DrillDownRequest = {
      kpi,
      level,
      filters,
      selectedValue,
      userContext,
      options: {
        includeIndividuals: level === 'individual',
        includeTrends: level === 'trend',
        limit: 100
      }
    };
    
    console.log('📋 Created request:', request);
    
    // Call the fetchDrillDownData function directly and return its result
    console.log('🚀 Calling fetchDrillDownData...');
    
    // Simulate API call with realistic delay for data processing  
    await new Promise(resolve => setTimeout(resolve, 1200 + Math.random() * 800)); // 1.2-2.0 seconds
    
    // Mock data generation based on request
    const mockData = generateMockDrillDownData(request);
    console.log('🎯 Mock data generated in navigate:', mockData);
    
    const response: DrillDownResponse = {
      success: true,
      data: mockData,
      metadata: {
        queryTime: Math.random() * 200 + 50,
        cacheHit: Math.random() > 0.7,
        recordCount: mockData.breakdowns.length,
        hasMore: mockData.breakdowns.length >= 50
      }
    };
    
    console.log('📦 Returning navigate response:', response);
    return response;
  }
);

const drilldownSlice = createSlice({
  name: 'drilldown',
  initialState,
  reducers: {
    // Set the current drill-down path
    setCurrentPath: (state, action: PayloadAction<DrillDownPath>) => {
      state.currentPath = action.payload;
      
      // Add to history if it's a new path
      if (!state.history.find(h => 
        h.kpi === action.payload.kpi && 
        h.level === action.payload.level &&
        JSON.stringify(h.filters) === JSON.stringify(action.payload.filters)
      )) {
        state.history.push(action.payload);
      }
    },
    
    // Navigate up one level (drill-up)
    drillUp: (state) => {
      if (!state.currentPath) return;
      
      const currentConfig = DRILL_DOWN_CONFIGS[state.currentPath.kpi];
      const currentIndex = currentConfig.levels.findIndex(l => l.level === state.currentPath!.level);
      
      if (currentIndex > 0) {
        const previousLevel = currentConfig.levels[currentIndex - 1];
        const newBreadcrumbs = state.currentPath.breadcrumbs.slice(0, -1);
        
        state.currentPath = {
          ...state.currentPath,
          level: previousLevel.level,
          breadcrumbs: newBreadcrumbs,
          selectedValue: undefined,
          filters: newBreadcrumbs.length > 0 ? newBreadcrumbs[newBreadcrumbs.length - 1].filters : []
        };
      }
    },
    
    // Navigate to specific level with breadcrumb click
    navigateToLevel: (state, action: PayloadAction<{ 
      breadcrumbIndex: number 
    }>) => {
      if (!state.currentPath) return;
      
      const { breadcrumbIndex } = action.payload;
      const targetBreadcrumb = state.currentPath.breadcrumbs[breadcrumbIndex];
      
      if (targetBreadcrumb) {
        state.currentPath = {
          ...state.currentPath,
          level: targetBreadcrumb.level,
          breadcrumbs: state.currentPath.breadcrumbs.slice(0, breadcrumbIndex + 1),
          filters: targetBreadcrumb.filters,
          selectedValue: targetBreadcrumb.value
        };
      }
    },
    
    // Add cross-filter from drill-down interaction
    addCrossFilter: (state, action: PayloadAction<DrillDownFilter>) => {
      if (!state.currentPath) return;
      
      const newFilter = action.payload;
      const existingFilterIndex = state.currentPath.filters.findIndex(
        f => f.dimension === newFilter.dimension
      );
      
      if (existingFilterIndex >= 0) {
        state.currentPath.filters[existingFilterIndex] = newFilter;
      } else {
        state.currentPath.filters.push(newFilter);
      }
    },
    
    // Remove cross-filter
    removeCrossFilter: (state, action: PayloadAction<string>) => {
      if (!state.currentPath) return;
      
      state.currentPath.filters = state.currentPath.filters.filter(
        f => f.dimension !== action.payload
      );
    },
    
    // Clear all drill-down state
    clearDrillDown: (state) => {
      state.currentPath = null;
      state.data = null;
      state.error = null;
      state.availableInteractions = [];
    },
    
    // Set available interactions based on current context
    setAvailableInteractions: (state, action) => {
      state.availableInteractions = action.payload;
    },
    
    // Log drill-down analytics event
    logDrillDownEvent: (state, action: PayloadAction<DrillDownEvent>) => {
      // In real implementation, this would send analytics to backend
      console.log('Drill-down event:', action.payload);
    },
    
    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    // Set error state
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    }
  },
  
  extraReducers: (builder) => {
    builder
      // Fetch drill-down data
      .addCase(fetchDrillDownData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDrillDownData.fulfilled, (state, action) => {
        console.log('✅ fetchDrillDownData.fulfilled called with payload:', action.payload);
        state.loading = false;
        const response = action.payload as unknown as DrillDownResponse;
        console.log('📋 Response in reducer:', response);
        if (response.success && response.data) {
          state.data = response.data;
          state.availableInteractions = DRILL_DOWN_CONFIGS[response.data.kpi].extraInteractions;
          console.log('💾 State updated with data:', state.data);
        } else {
          state.error = response.error || 'Failed to load drill-down data';
          console.error('❌ Error in response:', response.error);
        }
      })
      .addCase(fetchDrillDownData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load drill-down data';
      })
      
      // Navigate drill-down
      .addCase(navigateDrillDown.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(navigateDrillDown.fulfilled, (state, action) => {
        console.log('✅ navigateDrillDown.fulfilled called with payload:', action.payload);
        state.loading = false;
        const response = action.payload as DrillDownResponse;
        console.log('📋 Navigate response in reducer:', response);
        
        if (response.success && response.data) {
          state.data = response.data;
          console.log('💾 State updated with navigation data:', state.data);
          
          // Create initial path if none exists
          state.currentPath = {
            kpi: response.data.kpi,
            level: response.data.level,
            filters: [],
            breadcrumbs: [{
              level: response.data.level,
              label: response.data.level,
              filters: [],
              value: undefined
            }]
          };
          console.log('🆕 Created current path:', state.currentPath);
        } else {
          console.error('❌ Error in navigate response:', response.error);
        }
      })
      .addCase(navigateDrillDown.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to navigate drill-down';
      });
  }
});

// Helper function to generate mock data (in real app, this would come from API)
function generateMockDrillDownData(request: DrillDownRequest): DrillDownData {
  console.log('🔥 generateMockDrillDownData called with request:', request);
  
  const config = DRILL_DOWN_CONFIGS[request.kpi];
  const levelConfig = config.levels.find(l => l.level === request.level);
  
  console.log('📋 Found config:', config);
  console.log('📊 Level config:', levelConfig);
  
  if (!levelConfig) {
    console.error('❌ Invalid level config:', request.level, 'for KPI:', request.kpi);
    throw new Error(`Invalid level ${request.level} for KPI ${request.kpi}`);
  }
  
  // Generate mock breakdowns based on the level and filters
  const breakdowns = generateMockBreakdowns(request.kpi, request.level, request.filters);
  console.log('📈 Generated breakdowns:', breakdowns);
  
  // Generate mock summary
  const summary = generateMockSummary(request.kpi);
  console.log('📄 Generated summary:', summary);
  
  // Generate mock individuals if needed (for individual level or when drilling deep)
  const individuals = (request.options?.includeIndividuals || request.level === 'individual') ? 
    generateMockIndividuals(request.kpi, Math.min(25, 10 + Math.floor(Math.random() * 15))) : undefined;
  console.log('👥 Generated individuals:', individuals?.length || 0);
  
  // Generate mock trends if needed  
  const trends = request.options?.includeTrends ?
    generateMockTrends(request.kpi, 12) : undefined;
  console.log('📊 Generated trends:', trends?.length || 0);
  
  // Generate permissions based on user context
  const permissions = generateMockPermissions(request.userContext);
  console.log('🔐 Generated permissions:', permissions);
  
  const result = {
    level: request.level,
    kpi: request.kpi,
    summary,
    breakdowns,
    individuals,
    trends,
    permissions
  };
  
  console.log('✅ Final drill-down data:', result);
  return result;
}

function generateMockBreakdowns(kpi: KpiType, level: DrillDownLevel, filters: DrillDownFilter[] = []) {
  console.log('📊 Generating breakdowns for level:', level, 'with filters:', filters);
  
  // Check if we're drilling down from a specific department/geography
  const departmentFilter = filters.find(f => f.dimension.startsWith('department_'));
  const geographyFilter = filters.find(f => f.dimension.startsWith('geography_'));
  
  // Realistic mock data based on KPI and level
  let categories: { [key: string]: string[] } = {
    department: ['Medical Device Engineering', 'Clinical Operations', 'Data Analytics & AI', 'Regulatory Affairs', 'Quality Assurance', 'Software Development', 'Human Resources', 'Legal & Compliance'],
    geography: ['United States', 'United Kingdom', 'Germany', 'Canada', 'Singapore', 'Australia', 'Netherlands', 'France'],
    demographic: ['L1-L3 (Junior)', 'L4-L6 (Mid-Level)', 'L7-L8 (Senior)', 'L9-L10 (Staff)', 'L11+ (Executive)']
  };

  // Apply filtering logic based on current filters
  if (departmentFilter && level === 'geography') {
    // If filtering by Engineering, show more US/UK presence
    if (departmentFilter.value === 'Medical Device Engineering' || departmentFilter.value === 'Software Development') {
      categories.geography = ['United States', 'United Kingdom', 'Germany', 'Canada', 'Netherlands'];
    }
    // If filtering by Sales, show more global presence  
    else if (departmentFilter.value === 'Clinical Operations') {
      categories.geography = ['United States', 'United Kingdom', 'Germany', 'Singapore', 'Australia', 'France'];
    }
    // Other departments have smaller geographic footprint
    else {
      categories.geography = ['United States', 'United Kingdom', 'Germany'];
    }
  }

  if ((departmentFilter || geographyFilter) && level === 'demographic') {
    // Engineering tends to be more senior-heavy
    if (departmentFilter?.value === 'Medical Device Engineering' || departmentFilter?.value === 'Software Development') {
      categories.demographic = ['L4-L6 (Mid-Level)', 'L7-L8 (Senior)', 'L9-L10 (Staff)', 'L11+ (Executive)'];
    }
    // Sales has more junior staff
    else if (departmentFilter?.value === 'Sales') {
      categories.demographic = ['L1-L3 (Junior)', 'L4-L6 (Mid-Level)', 'L7-L8 (Senior)'];
    }
    // Others have mixed distribution
    else {
      categories.demographic = ['L1-L3 (Junior)', 'L4-L6 (Mid-Level)', 'L7-L8 (Senior)', 'L9-L10 (Staff)'];
    }
  }
  
  const baseCategories = level === 'department' ? categories.department :
                        level === 'geography' ? categories.geography :
                        categories.demographic;
  
  // Generate realistic data based on KPI type
  return baseCategories.map((category, index) => {
    const nextLevel = getNextLevel(kpi, level);
    let value, percentage, count, trend;
    
    // Realistic data patterns based on KPI type
    switch (kpi) {
      case 'totalEmployees':
        if (level === 'department') {
          const deptSizes = [450, 380, 220, 85, 156, 234, 167, 98]; // Engineering largest
          value = deptSizes[index] || Math.floor(Math.random() * 200) + 50;
          count = value;
          percentage = (value / 1790) * 100; // Total workforce
        } else if (level === 'geography') {
          const geoSizes = [890, 245, 178, 156, 134, 89, 67, 45];
          value = geoSizes[index] || Math.floor(Math.random() * 100) + 20;
          count = value;
          percentage = (value / 1790) * 100;
        } else {
          const levelSizes = [567, 645, 378, 156, 44]; // More junior employees
          value = levelSizes[index] || Math.floor(Math.random() * 150) + 30;
          count = value;
          percentage = (value / 1790) * 100;
        }
        trend = index < 3 ? 'up' : (index === 4 ? 'stable' : 'down');
        break;
        
      case 'attritionRisk':
        if (level === 'demographic') {
          const riskLevels = ['High Risk (>15%)', 'Medium Risk (8-15%)', 'Low Risk (<8%)'];
          const riskPercentages = [18.5, 11.2, 6.8];
          value = riskPercentages[index] || Math.random() * 25;
          percentage = value;
          count = Math.floor((value / 100) * 1790);
          trend = index === 0 ? 'up' : 'down'; // High risk trending up
        } else {
          const deptRisk = [12.4, 15.8, 9.2, 7.1, 8.9, 11.3, 10.5, 6.8];
          value = level === 'department' ? (deptRisk[index] || Math.random() * 20) : Math.random() * 25;
          percentage = value;
          count = Math.floor((value / 100) * (level === 'department' ? [450, 380, 220, 85, 156, 234, 167, 98][index] : 200));
          trend = value > 12 ? 'up' : (value > 8 ? 'stable' : 'down');
        }
        break;
        
      case 'engagementScore':
        const engagementScores = level === 'department' ? 
          [8.9, 7.2, 8.1, 8.8, 7.9, 7.6, 8.3, 8.0] : // Departments
          level === 'geography' ?
          [8.1, 7.8, 8.4, 8.2, 7.9, 8.0, 8.3, 7.7] : // Geography
          [7.2, 8.1, 8.6, 8.9, 9.1]; // Job levels (higher levels more engaged)
        value = engagementScores[index] || (7 + Math.random() * 2);
        percentage = (value / 10) * 100;
        count = Math.floor(percentage * 10); // Survey responses
        trend = value > 8.5 ? 'up' : (value > 7.5 ? 'stable' : 'down');
        break;
        
      case 'leadershipReady':
        const readinessPercentages = level === 'department' ?
          [78, 65, 71, 85, 82, 69, 74, 88] : // Departments
          level === 'geography' ?
          [73, 76, 71, 74, 69, 72, 75, 68] : // Geography
          [45, 68, 78, 85, 92]; // Job levels (higher levels more ready)
        value = readinessPercentages[index] || Math.floor(Math.random() * 40) + 50;
        percentage = value;
        count = Math.floor((value / 100) * 120); // Total leadership roles
        trend = value > 80 ? 'up' : (value > 65 ? 'stable' : 'down');
        break;
        
      case 'timeToFill':
        const fillTimes = level === 'department' ?
          [52, 28, 35, 41, 38, 45, 33, 47] : // Engineering takes longest
          level === 'geography' ?
          [32, 38, 29, 35, 41, 33, 31, 36] : // Geography variation
          [25, 32, 38, 45, 62]; // Higher levels take longer
        value = fillTimes[index] || Math.floor(Math.random() * 30) + 25;
        percentage = ((60 - value) / 60) * 100; // Inverse - lower is better
        count = Math.floor(Math.random() * 20) + 5; // Open positions
        trend = value < 30 ? 'up' : (value < 45 ? 'stable' : 'down');
        break;
        
      case 'diversityScore':
        const diversityScores = level === 'department' ?
          [71, 68, 79, 85, 74, 66, 82, 77] : // HR highest, Ops lowest
          level === 'geography' ?
          [74, 72, 69, 76, 81, 73, 78, 70] : // Geographic variation
          [82, 76, 71, 68, 58]; // Diversity decreases at higher levels
        value = diversityScores[index] || Math.floor(Math.random() * 30) + 60;
        percentage = value;
        count = Math.floor((value / 100) * 1790); // Diverse employees
        trend = value > 75 ? 'up' : (value > 65 ? 'stable' : 'down');
        break;
        
      default:
        value = Math.floor(Math.random() * 1000) + 100;
        percentage = Math.random() * 100;
        count = Math.floor(Math.random() * 500) + 50;
        trend = ['up', 'down', 'stable'][Math.floor(Math.random() * 3)] as 'up' | 'down' | 'stable';
    }
    
    return {
      category,
      value: Math.round(value * 10) / 10, // Round to 1 decimal
      percentage: Math.round(percentage * 10) / 10,
      count,
      trend: trend as 'up' | 'down' | 'stable',
      canDrillDown: index < 6, // Allow drill-down for most items
      nextLevel: nextLevel || undefined,
      metadata: {
        lastUpdated: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(), // Last week as string
        confidence: 0.75 + Math.random() * 0.2 // 75-95% confidence
      }
    };
  });
}

function generateMockSummary(kpi: KpiType) {
  const summaries = {
    totalEmployees: { value: DataConsistencyService.getTotalEmployees(), unit: '', target: 135000, change: 2.3, changeType: 'increase', benchmark: 128500 },
    attritionRisk: { value: 11.4, unit: '%', target: 8.0, change: -1.2, changeType: 'decrease', benchmark: 13.2 },
    engagementScore: { value: 8.1, unit: '/10', target: 8.5, change: 0.4, changeType: 'increase', benchmark: 7.6 },
    leadershipReady: { value: 73, unit: '%', target: 80, change: 5.2, changeType: 'increase', benchmark: 68 },
    timeToFill: { value: 38, unit: ' days', target: 30, change: -4.0, changeType: 'decrease', benchmark: 42 },
    diversityScore: { value: 74, unit: '%', target: 85, change: 1.8, changeType: 'increase', benchmark: 71 }
  };
  
  const base = summaries[kpi];
  const status = (() => {
    switch (kpi) {
      case 'attritionRisk':
      case 'timeToFill':
        // Lower is better for these metrics
        return base.value <= base.target ? 'good' : 
               base.value <= base.target * 1.3 ? 'warning' : 'critical';
      default:
        // Higher is better for other metrics
        return base.value >= base.target * 0.9 ? 'good' : 
               base.value >= base.target * 0.7 ? 'warning' : 'critical';
    }
  })();
  
  return {
    value: base.value,
    unit: base.unit,
    target: base.target,
    change: base.change,
    changeType: base.changeType as 'increase' | 'decrease',
    benchmark: base.benchmark,
    status: status as 'good' | 'warning' | 'critical'
  };
}

function generateMockIndividuals(kpi: KpiType, count: number) {
  // Generate realistic mock employee records
  const names = [
    'Sarah Chen', 'Michael Rodriguez', 'Emily Johnson', 'David Kim', 'Jessica Williams', 
    'Alex Thompson', 'Maria Garcia', 'James Wilson', 'Ashley Davis', 'Ryan Anderson',
    'Priya Patel', 'Chris Martinez', 'Lauren Taylor', 'Kevin Lee', 'Amanda Brown'
  ];
  
  const departments = getAllDepartments();
  const locations = getAllLocations();
  const countries = locations.map(loc => loc.split(',')[1]?.trim() || loc).slice(0, 8);
  const titles = {
    Engineering: ['Software Engineer', 'Senior Engineer', 'Principal Engineer', 'Engineering Manager', 'Staff Engineer'],
    Sales: ['Sales Rep', 'Senior Sales Rep', 'Sales Manager', 'Account Executive', 'VP Sales'],
    Marketing: ['Marketing Specialist', 'Marketing Manager', 'Product Marketing Manager', 'CMO', 'Growth Manager'],
    HR: ['HR Generalist', 'HR Business Partner', 'Talent Acquisition', 'People Operations', 'Chief People Officer'],
    Finance: ['Financial Analyst', 'Senior Analyst', 'Finance Manager', 'Controller', 'CFO'],
    Operations: ['Operations Specialist', 'Operations Manager', 'Program Manager', 'VP Operations', 'COO']
  };
  
  return Array.from({ length: count }, (_, i) => {
    const dept = departments[i % departments.length];
    const jobLevel = Math.floor(Math.random() * 10) + 1;
    const tenure = Math.random() * 8 + 0.5; // 0.5 to 8.5 years
    const engagementScore = 6 + Math.random() * 4; // 6-10 range
    
    // Realistic correlations
    const attritionRisk = (() => {
      if (engagementScore < 7 && tenure < 2) return 'high';
      if (engagementScore < 8 && tenure < 1) return 'medium';
      if (engagementScore > 8.5 && tenure > 3) return 'low';
      return ['low', 'medium', 'high'][Math.floor(Math.random() * 3)] as 'low' | 'medium' | 'high';
    })();
    
    const isSuccessor = jobLevel > 6 && engagementScore > 8 && Math.random() > 0.7;
    
    return {
      id: `emp_${String(i + 1).padStart(4, '0')}`,
      displayName: names[i % names.length] + (i >= names.length ? ` ${Math.floor(i / names.length) + 1}` : ''),
      department: dept,
      jobLevel: `L${jobLevel}`,
      jobTitle: titles[dept as keyof typeof titles]?.[Math.min(Math.floor(jobLevel / 2), 4)] || 'Specialist',
      country: countries[i % countries.length],
      tenure: Math.round(tenure * 10) / 10,
      attritionRisk,
      engagementScore: Math.round(engagementScore * 10) / 10,
      performanceRating: ['Exceeds', 'Meets', 'Needs Improvement'][Math.floor(Math.random() * 3)],
      isSuccessor,
      lastPromotion: tenure > 2 ? `${Math.floor(Math.random() * 24) + 1} months ago` : 'Never',
      skillGaps: Math.floor(Math.random() * 3),
      mentorshipProgram: Math.random() > 0.6,
      diversityGroup: ['Gender', 'Ethnicity', 'Age', 'Disability', 'LGBTQ+', 'Veteran'][Math.floor(Math.random() * 6)],
      visibleFields: ['department', 'jobLevel', 'jobTitle', 'country', 'tenure', 'performanceRating']
    };
  });
}

function generateMockTrends(kpi: KpiType, months: number) {
  const summary = generateMockSummary(kpi);
  const baseValue = summary.value;
  const target = summary.target;
  const benchmark = summary.benchmark;
  
  // Create realistic trend patterns
  return Array.from({ length: months }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - (months - i - 1));
    
    // Seasonal and growth patterns
    const monthProgress = i / (months - 1);
    const seasonality = Math.sin((i / months) * 2 * Math.PI) * 0.1;
    const trendDirection = summary.changeType === 'increase' ? 0.15 : -0.15;
    const randomVariation = (Math.random() - 0.5) * 0.1;
    
    let value: number;
    
    switch (kpi) {
      case 'totalEmployees':
        // Steady growth with some hiring seasons
        value = baseValue * (0.85 + monthProgress * 0.15 + seasonality + randomVariation);
        break;
        
      case 'attritionRisk':
        // Cyclical pattern with improvement over time
        value = baseValue * (1.1 - monthProgress * 0.15 + seasonality * 0.5 + randomVariation);
        break;
        
      case 'engagementScore':
        // Generally improving with dips during busy periods
        value = baseValue * (0.9 + monthProgress * 0.12 - seasonality * 0.3 + randomVariation);
        break;
        
      case 'leadershipReady':
        // Gradual improvement as programs mature
        value = baseValue * (0.85 + monthProgress * 0.2 + randomVariation);
        break;
        
      case 'timeToFill':
        // Improving processes reduce time to fill
        value = baseValue * (1.15 - monthProgress * 0.2 + seasonality * 0.8 + randomVariation);
        break;
        
      case 'diversityScore':
        // Steady improvement with occasional plateaus
        value = baseValue * (0.88 + monthProgress * 0.15 + Math.sin(monthProgress * Math.PI) * 0.05 + randomVariation);
        break;
        
      default:
        value = baseValue + (Math.random() - 0.5) * baseValue * 0.2;
    }
    
    // Ensure value makes sense for the metric
    value = Math.max(0, value);
    if (kpi === 'engagementScore') value = Math.min(10, value);
    if (['attritionRisk', 'leadershipReady', 'diversityScore'].includes(kpi)) {
      value = Math.max(0, Math.min(100, value));
    }
    
    return {
      date: date.toISOString(), // Convert to string for Redux serialization
      value: Math.round(value * 100) / 100,
      period: 'monthly',
      benchmark: benchmark,
      target: target
    };
  });
}

function generateMockPermissions(userContext: any) {
  return {
    canViewIndividuals: userContext.permissions.includes('viewIndividualData'),
    canViewNames: userContext.permissions.includes('viewEmployeeNames'),
    canViewSensitiveData: userContext.permissions.includes('viewSensitiveData'),
    canExport: userContext.permissions.includes('exportData'),
    allowedDepartments: userContext.role === 'HR_LEADER' ? 
      ['Engineering', 'Sales', 'Marketing', 'HR', 'Finance', 'Operations'] :
      [userContext.department || 'Medical Device Engineering'],
    allowedCountries: ['US', 'UK', 'DE', 'CA', 'SG'],
    maxDrillDownLevel: userContext.permissions.includes('viewIndividualData') ? 
      'individual' as DrillDownLevel : 'demographic' as DrillDownLevel
  };
}

export const {
  setCurrentPath,
  drillUp,
  navigateToLevel,
  addCrossFilter,
  removeCrossFilter,
  clearDrillDown,
  setAvailableInteractions,
  logDrillDownEvent,
  setLoading,
  setError
} = drilldownSlice.actions;

export default drilldownSlice.reducer;

// Selectors
export const selectDrillDownState = (state: { drilldown: DrillDownState }) => state.drilldown;
export const selectCurrentPath = (state: { drilldown: DrillDownState }) => state.drilldown.currentPath;
export const selectDrillDownData = (state: { drilldown: DrillDownState }) => state.drilldown.data;
export const selectDrillDownLoading = (state: { drilldown: DrillDownState }) => state.drilldown.loading;
export const selectDrillDownError = (state: { drilldown: DrillDownState }) => state.drilldown.error;
export const selectAvailableInteractions = (state: { drilldown: DrillDownState }) => state.drilldown.availableInteractions;